import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { PROFILE } from "../lib/data";
import { Phone, MessageCircle, MapPin, Clock, Mail, Instagram, Facebook, Twitter, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border border-[#262626] rounded-sm px-4 py-3.5 text-[#F7F5F2] placeholder:text-[#5c5c5c] focus:outline-none focus:border-[#C8A24B] transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        subject: form.subject || "General Enquiry",
      });
      toast.success("Enquiry received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="border-t border-[#262626] bg-[#0A0A0A]" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-36 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="overline">Enquire</span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] mt-5 text-[#F7F5F2] text-balance">
              Book a consultation.
            </h2>
            <p className="text-[#8C8C8C] mt-5 max-w-md">
              Share a few details about your matter and we will respond promptly. For urgent matters, call or WhatsApp directly.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-name" className={FIELD} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input data-testid="contact-phone" className={FIELD} placeholder="Mobile number" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-email" type="email" className={FIELD} placeholder="Email address" value={form.email} onChange={set("email")} />
                <input data-testid="contact-subject" className={FIELD} placeholder="Nature of matter" value={form.subject} onChange={set("subject")} />
              </div>
              <textarea data-testid="contact-message" rows={5} className={FIELD + " resize-none"} placeholder="Briefly describe your matter" value={form.message} onChange={set("message")} />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-sm bg-[#F7F5F2] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-[#C8A24B] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={1.75} />}
                {loading ? "Sending…" : "Submit Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href={`tel:${PROFILE.phoneTel}`} data-testid="contact-call" className="group flex flex-col justify-between border border-[#262626] rounded-sm p-6 hover:bg-[#121212] hover:border-[#C8A24B]/50 transition-colors duration-300 min-h-[130px]">
                <Phone className="text-[#C8A24B]" size={22} strokeWidth={1.5} />
                <div><p className="overline">Call</p><p className="text-[#F7F5F2] mt-1">{PROFILE.phoneDisplay}</p></div>
              </a>
              <a href={PROFILE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="group flex flex-col justify-between border border-[#262626] rounded-sm p-6 hover:bg-[#121212] hover:border-[#C8A24B]/50 transition-colors duration-300 min-h-[130px]">
                <MessageCircle className="text-[#C8A24B]" size={22} strokeWidth={1.5} />
                <div><p className="overline">WhatsApp</p><p className="text-[#F7F5F2] mt-1">Chat with us</p></div>
              </a>
            </div>

            <a href={PROFILE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-directions" className="group mt-4 flex items-start gap-4 border border-[#262626] rounded-sm p-6 hover:bg-[#121212] hover:border-[#C8A24B]/50 transition-colors duration-300">
              <MapPin className="text-[#C8A24B] shrink-0 mt-1" size={22} strokeWidth={1.5} />
              <div>
                <p className="overline">Chamber · Get Directions</p>
                <p className="text-[#F7F5F2] mt-2 leading-relaxed">{PROFILE.address.line1}</p>
                <p className="text-[#8C8C8C] text-sm leading-relaxed">{PROFILE.address.line2}</p>
                <p className="text-[#8C8C8C] text-sm leading-relaxed">{PROFILE.address.line3}</p>
              </div>
            </a>

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 border border-[#262626] rounded-sm p-6">
                <Clock className="text-[#C8A24B] shrink-0" size={22} strokeWidth={1.5} />
                <div><p className="overline">Working Hours</p><p className="text-[#F7F5F2] mt-1 text-sm">{PROFILE.hours}</p></div>
              </div>
              <a href={`mailto:${PROFILE.email}`} data-testid="contact-email-tile" className="group flex items-center gap-4 border border-[#262626] rounded-sm p-6 hover:bg-[#121212] hover:border-[#C8A24B]/50 transition-colors duration-300">
                <Mail className="text-[#C8A24B] shrink-0" size={22} strokeWidth={1.5} />
                <div><p className="overline">Email</p><p className="text-[#F7F5F2] mt-1 text-sm break-all">{PROFILE.email}</p></div>
              </a>
            </div>

            <div className="mt-4 flex gap-4">
              <a href={PROFILE.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="flex-1 flex items-center justify-center gap-2 border border-[#262626] rounded-sm py-4 text-[#8C8C8C] hover:text-[#C8A24B] hover:border-[#C8A24B]/50 transition-colors duration-300">
                <Instagram size={18} strokeWidth={1.5} /> Instagram
              </a>
              <a href={PROFILE.twitter} target="_blank" rel="noopener noreferrer" data-testid="contact-twitter" className="flex-1 flex items-center justify-center gap-2 border border-[#262626] rounded-sm py-4 text-[#8C8C8C] hover:text-[#C8A24B] hover:border-[#C8A24B]/50 transition-colors duration-300">
                <Twitter size={18} strokeWidth={1.5} /> X (Twitter)
              </a>
              <a href={PROFILE.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" className="flex-1 flex items-center justify-center gap-2 border border-[#262626] rounded-sm py-4 text-[#8C8C8C] hover:text-[#C8A24B] hover:border-[#C8A24B]/50 transition-colors duration-300">
                <Facebook size={18} strokeWidth={1.5} /> Facebook
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
